import React from 'react';
import { Button, ButtonProps } from '../button/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Typography } from '../typography';
import clsx from 'clsx';
import { Option } from '@/interfaces';
import { Icon } from '../icon';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';

type TriggerType = 'button' | 'icon-menu';

interface Props {
	buttonLabel?: string;
	classNameButtonLabel?: string;
	listOptions: Option[];
	decoration?: ButtonProps['decoration'];
	size?: ButtonProps['size'];
	classNameDropdownMenuContent?: string;
	classNameList?: string;
	disabled?: boolean;
	align?: 'center' | 'end' | 'start' | undefined;
	addNewLabel?: string;
	iconLeft?: string;
	onClick?: () => void;
	onScroll?: (e: any) => void;
	triggerType?: TriggerType;
	classNameTrigger?: string;
	classNameButtonTrigger?: string;
	closeModalAfterClick?: boolean;
	customButton?: React.ReactNode;
}

export const ButtonDropdownList: React.FC<Props> = ({
	listOptions,
	buttonLabel = '',
	classNameButtonLabel,
	decoration = 'primary',
	size = 'medium',
	addNewLabel,
	classNameDropdownMenuContent,
	disabled,
	align,
	iconLeft,
	onClick,
	onScroll,
	triggerType = 'button',
	classNameTrigger,
	classNameButtonTrigger,
	closeModalAfterClick,
	customButton,
	classNameList,
}) => {
	const { Modal, show } = useModal();
	const [open, setOpen] = React.useState(false);
	return (
		<>
			<DropdownMenu
				modal={false}
				open={open}
				onOpenChange={() => {
					if (!disabled && listOptions.length > 0) setOpen(!open);
				}}
			>
				<div className="relative w-full md:w-auto hidden md:block">
					<DropdownMenuTrigger asChild>
						<div
							className={clsx(
								'w-full md:w-auto outline-none',
								classNameTrigger
							)}
						>
							{triggerType === 'icon-menu' ? (
								<button
									type="button"
									className="p-1 rounded-md hover:bg-dark-10 transition-colors"
									disabled={disabled}
								>
									<Icon src={Icons.dotsMenuVertical} className="!size-3" />
								</button>
							) : customButton ? (
								customButton
							) : (
								<Button
									type="button"
									size={size}
									decoration={decoration}
									disabled={disabled}
									onClick={() => {
										if (listOptions.length === 0) {
											onClick?.();
										}
									}}
									className={classNameButtonTrigger}
								>
									<div className="flex items-center justify-end">
										{iconLeft && (
											<span className={clsx('size-4 mr-2 z-50')}>
												<Icon src={iconLeft} />
											</span>
										)}
										<p
											className={clsx(
												classNameButtonLabel,
												'font-semibold text-base'
											)}
										>
											<span>{buttonLabel}</span>
										</p>
										{listOptions.length > 0 && (
											<span className="size-5 ml-2 z-50">
												<Icon src={open ? Icons.arrowUp : Icons.arrowDown} />
											</span>
										)}
									</div>
								</Button>
							)}
						</div>
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align={align}
						className={clsx(
							'p-0 py-4 rounded-3xl w-[224px]',
							classNameDropdownMenuContent
						)}
					>
						<DropdownMenuLabel className="px-0">
							<ul
								onScroll={onScroll}
								className={clsx(
									'max-h-[180px] overflow-y-auto scroll-custom',
									classNameList
								)}
							>
								{listOptions.map((item, index) => (
									<li
										key={'option-list-' + item.label + '-' + index}
										className={clsx(
											'flex items-center gap-x-2 py-2 px-4',
											item.disabled
												? 'opacity-40 cursor-not-allowed'
												: 'cursor-pointer hover:bg-dark-10',
											item.className
										)}
										onClick={(e) => {
											if (item.disabled) return;
											item?.onClick?.(e);
											if (closeModalAfterClick) {
												setOpen(false);
											}
										}}
									>
										{item.icon && (
											<Icon
												src={item.icon}
												className={clsx(
													'!size-4 text-accent-shade',
													item.classNameItemIcon
												)}
											/>
										)}
										<div>
											<Typography type="regular-16">{item.label}</Typography>
											<Typography type="regular-14" className="text-grey-30">
												{item.labelAux}
											</Typography>
										</div>
									</li>
								))}
							</ul>
							{addNewLabel && (
								<button
									className="flex items-center py-2 gap-x-2 w-full"
									onClick={onClick}
								>
									<Icon
										src={Icons.addCircle}
										className="!size-4 text-primary"
									/>
									<Typography type="regular-16">{addNewLabel}</Typography>
								</button>
							)}
						</DropdownMenuLabel>
					</DropdownMenuContent>
				</div>
			</DropdownMenu>
			<Button
				iconRight
				label={buttonLabel}
				classNameLabel={classNameButtonLabel}
				decoration="secondary"
				size="medium"
				className="block md:hidden"
				disabled={disabled}
				onClick={() => show()}
			/>
			<Modal
				withXButton
				title={buttonLabel}
				classNameTitle="text-left"
				classNameCardContainer="min-w-[343px] bg-white"
				classNameContainerChildren="!px-0"
			>
				<ul
					onScroll={onScroll}
					className="max-h-[180px] overflow-y-auto scroll-custom"
				>
					{listOptions.map((item, index) => (
						<li
							key={'option-list-' + item.label + '-' + index}
							className={clsx(
								'flex items-center gap-x-2 py-2',
								item.disabled
									? 'opacity-40 cursor-not-allowed'
									: 'cursor-pointer'
							)}
							onClick={item.disabled ? undefined : item.onClick}
						>
							{item.icon && (
								<Icon src={item.icon} className="!size-4 text-accent-shade" />
							)}
							<div>
								<Typography type="regular-16">{item.label}</Typography>
								<Typography
									type="regular-14"
									className="text-grey-30 text-left"
								>
									{item.labelAux}
								</Typography>
							</div>
						</li>
					))}
				</ul>
				{addNewLabel && (
					<div className="space-y-3 mt-3">
						<Button
							decoration="primary"
							className="flex items-center py-2 gap-x-2 w-full"
							onClick={onClick}
							iconLeft
							icon={Icons.addCircle}
							label={addNewLabel}
						/>
						<Button decoration="secondary">Cancel</Button>
					</div>
				)}
			</Modal>
		</>
	);
};
